import React, { useEffect, useState } from "react";

import { Button, Card, Select } from "antd";
import { Meta } from "antd/es/list/Item";
import { getFeatureFlagsConfiguration } from "../../utils/getGlobalConfiguration";
import { useHistory } from "react-router-dom";
import { useAtom } from "jotai";
import { featureFlagsChangeAtom } from "../../atoms/atoms";
import { useSelector } from "react-redux";
import { State } from "../../configureStore";

type DemoConfig = {
  key: string;
  versionKey: string;
  title: string;
  files: { release: string; dev: string };
};

const getContextPath = () => {
  const path = globalThis.location.pathname;
  return path.substring(0, path.lastIndexOf("/demos"));
};

const DEMOS: DemoConfig[] = [
  {
    key: "sales-flow-widget",
    versionKey: "sales-flow-widget",
    title: "Sales Flow Widget",
    files: { release: "/demo/sales-flow-widget.html", dev: "/demo/sales-flow-widget-dev.html" },
  },
  {
    key: "dynamic-widget",
    versionKey: "custom-widget",
    title: "Dynamic Widget",
    files: { release: "/demo/dynamic-widget.html", dev: "/demo/dynamic-widget-dev.html" },
  },
];

const useDemoVersion = (versionKey: string, demoKey: string, releaseType: "release" | "development") => {
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const file = releaseType === "release" ? `/demo/${demoKey}.html` : `/demo/${demoKey}-dev.html`;

  const contextPath = getContextPath();
  const fileWithContextPath = `${contextPath}${file}`;

  useEffect(() => {
    setError(null);

    fetch(fileWithContextPath)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const pattern = `${versionKey}/([0-9]+(?:.[0-9]+)*|development)`;
        const regex = new RegExp(pattern, "i");
        const execResult = regex.exec(text);
        setVersion(execResult ? execResult[1] : null);
      })
      .catch((err) => {
        setError(err?.message ?? "failed");
        setVersion(null);
      });
  }, [demoKey, releaseType]);

  return { version, error };
};

export const Demos = () => {
  const history = useHistory();
  const [featureFlagValues] = useAtom(featureFlagsChangeAtom);
  const isLoading = useSelector((state: State) => state.api.configurations.agentConfigurations.isFetching);

  useEffect(() => {
    if (isLoading || !featureFlagValues) {
      return;
    }

    if (!getFeatureFlagsConfiguration("demos.enabled")) {
      history.replace("/");
    }
  }, [isLoading, featureFlagValues, history]);

  const [releaseType, setReleaseType] = useState<"release" | "development">("development");

  if (isLoading || !featureFlagValues) {
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 600 }}>Release type</div>
        <Select
          value={releaseType}
          onChange={(value) => setReleaseType(value)}
          style={{ width: 140 }}
          options={[
            { label: "Development", value: "development" },
            { label: "Release", value: "release" },
          ]}
        />
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {DEMOS.map((demo) => (
          <DemoCard key={demo.key} demo={demo} releaseType={releaseType} />
        ))}
      </div>
    </div>
  );
};

const DemoCard = ({ demo, releaseType }: { demo: DemoConfig; releaseType: "release" | "development" }) => {
  const { version } = useDemoVersion(demo.versionKey, demo.key, releaseType);
  const contextPath = getContextPath();
  const demoFilePath = `${contextPath}${releaseType === "release" ? demo.files.release : demo.files.dev}`;

  return (
    <div
      style={{
        width: "340px",
        flex: "1 1 340px",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Card
        title={demo.title}
        actions={[
          <Button key={demo.key} onClick={() => globalThis.open(demoFilePath, "_blank")}>
            Open
          </Button>,
        ]}
      >
        <Meta
          description={
            <span style={{ padding: 15 }}>
              Version: <b>{version ?? "Loading..."}</b>
            </span>
          }
        />
      </Card>
    </div>
  );
};
